/**
 * OAuth Callback Page
 *
 * Google/GitHub redirects here with ?code=xxx&state=xxx
 * Supports two modes:
 * - Full-page redirect: stores token and navigates normally
 * - Popup: sends postMessage to parent window and closes itself
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authUtils } from "@lib/api";
import { client } from "@lib/auth";
import type { OAuthExchangeResult } from "@nestmold/web-auth-center/types";

function isPopup(): boolean {
  return window.opener != null && window.opener !== window;
}

function isOwnUrl(url: string): boolean {
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

function notifyParentAndClose(data: { ok: boolean; error?: string; data?: Record<string, unknown> }) {
  if (window.opener) {
    window.opener.postMessage({ type: "user-center-oauth", ...data }, "*");
  }
  window.close();
}

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error");

    if (oauthError) {
      const msg = params.get("error_description") || oauthError;
      if (isPopup()) {
        notifyParentAndClose({ ok: false, error: msg });
        return;
      }
      setError(msg);
      return;
    }

    if (!code || !state) {
      const msg = "OAuth回调缺少参数";
      if (isPopup()) {
        notifyParentAndClose({ ok: false, error: msg });
        return;
      }
      setError(msg);
      return;
    }

    const provider = detectProvider(state);

    client.exchangeOAuthCode(provider, code, state)
      .then((result: OAuthExchangeResult) => {
        // Popup mode
        if (isPopup()) {
          // New user must complete signup first. Do it IN the popup so the OIDC
          // arid + bind token (in this popup's sessionStorage) are preserved —
          // closing the popup here loses them and the user can't return to the app.
          if (result.next_step === "complete_signup") {
            sessionStorage.setItem("oauth_complete_signup", JSON.stringify({
              oauth_bind_token: result.oauth_bind_token,
              email: result.email,
              nickname: result.nickname,
              provider: result.provider,
            }));
            window.location.href = "/complete-signup";
            return;
          }
          // Existing user with pending OIDC arid → consent in popup
          const pendingArid = sessionStorage.getItem('oidc_auth_request_id');
          if (pendingArid) {
            sessionStorage.removeItem('oidc_auth_request_id');
            if (result.access_token) {
              authUtils.setToken(result.access_token);
              authUtils.setRefreshToken(result.refresh_token || "", result.expires_in || 0);
              authUtils.setUser({
                display_id: result.display_id,
                email: result.email || "",
                nickname: result.nickname || "",
                role: "user",
              });
            }
            window.location.href = `/consent?auth_request_id=${encodeURIComponent(pendingArid)}`;
            return;
          }
          // Must bind phone BEFORE returning token to parent.
          // Navigate popup to /bind-phone; after binding, BindPhone will notifyParentAndClose.
          if (result.access_token && (result as any).must_bind_phone) {
            authUtils.setToken(result.access_token);
            authUtils.setRefreshToken(result.refresh_token || "", result.expires_in || 0);
            authUtils.setUser({
              display_id: result.display_id,
              email: result.email || "",
              nickname: result.nickname || "",
              role: "user",
            });
            const redirectUri = (result as any).redirect_uri;
            if (redirectUri) sessionStorage.setItem('pending_redirect_uri', redirectUri);
            window.location.href = '/bind-phone';
            return;
          }
          // Non-OIDC popup: return token to parent and close
          notifyParentAndClose({
            ok: true,
            data: {
              access_token: result.access_token,
              refresh_token: result.refresh_token || "",
              expires_in: result.expires_in || 0,
              display_id: result.display_id,
              email: result.email || "",
              nickname: result.nickname || "",
            },
          });
          return;
        }

        // Full-page mode: store token and navigate
        if (result.next_step === "complete_signup") {
          sessionStorage.setItem("oauth_complete_signup", JSON.stringify({
            oauth_bind_token: result.oauth_bind_token,
            email: result.email,
            nickname: result.nickname,
            provider: result.provider,
          }));
          navigate("/complete-signup", { replace: true });
          return;
        }

        // Third-party redirect (no access_token in response — token is in the uc_code).
        // This MUST be checked BEFORE the access_token block, because third-party flow
        // returns redirect_uri without access_token.
        const tpRedirectUri = (result as any).redirect_uri;
        if (tpRedirectUri && !isOwnUrl(tpRedirectUri) && !result.access_token) {
          window.location.href = tpRedirectUri;
          return;
        }

        if (result.access_token) {

          authUtils.setToken(result.access_token);
          authUtils.setRefreshToken(result.refresh_token || "", result.expires_in || 0);
          authUtils.setUser({
            display_id: result.display_id,
            email: result.email || "",
            nickname: result.nickname || "",
            role: "user",
          });

          // Must bind phone BEFORE anything else.
          if ((result as any).must_bind_phone) {
            const bindRedirectUri = (result as any).redirect_uri;
            if (bindRedirectUri) sessionStorage.setItem('pending_redirect_uri', bindRedirectUri);
            const pendingArid = sessionStorage.getItem('oidc_auth_request_id');
            const qs = pendingArid ? `?auth_request_id=${encodeURIComponent(pendingArid)}` : '';
            window.location.href = `/bind-phone${qs}`;
            return;
          }

          // Third-party redirect (when access_token IS present alongside redirect_uri)
          if (tpRedirectUri && !isOwnUrl(tpRedirectUri)) {
            window.location.href = tpRedirectUri;
            return;
          }

          // OIDC flow: redirect to consent if pending auth_request_id exists
          const pendingArid = sessionStorage.getItem('oidc_auth_request_id');
          if (pendingArid) {
            sessionStorage.removeItem('oidc_auth_request_id');
            navigate(`/consent?auth_request_id=${encodeURIComponent(pendingArid)}`, { replace: true });
            return;
          }

          navigate("/identities", { replace: true });
          return;
        }

        setError("OAuth登录失败：未获取到令牌");
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "OAuth登录失败";
        if (isPopup()) {
          notifyParentAndClose({ ok: false, error: msg });
          return;
        }
        setError(msg);
      });
  }, [navigate]);

  function detectProvider(stateJwt: string): string {
    try {
      const parts = stateJwt.split(".");
      if (parts.length >= 2) {
        const payload = JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/")));
        if (payload.provider) return payload.provider;
      }
    } catch { /* ignore */ }
    return "google";
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={() => (window.location.href = "/login")}
            className="nm-btn px-6 py-2 rounded-lg"
          >
            返回登录
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="text-ink-muted">正在处理登录...</div>
      </div>
    </div>
  );
}
